import { IonCard, IonCardContent, IonIcon, IonSpinner } from '@ionic/react';
import { LibraryMetricValueSkeleton } from '../../../components/dashboard/HomepageSkeletons';
import {
  libraryOutline,
  medkitOutline,
  peopleOutline,
  pricetagsOutline,
  warningOutline,
} from 'ionicons/icons';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useLibraryStats } from '../../../context/LibraryStatsContext';
import { once, run } from '../../../helper/electron';
import './LibraryWidgets.scss';

// Shared type for statistics
interface TrackStatistics {
  totalFiles: number;
  totalArtists: number;
  totalAlbums: number;
  totalGenres: number;
  duplicates: number;
}

// Base widget for displaying a single metric
type StatWidgetProps = {
  editing: boolean;
  icon: string;
  value: React.ReactNode;
  label: string;
  filterType?: string;
};
export const StatWidget: React.FC<StatWidgetProps> = ({
  editing,
  icon,
  value,
  label,
  filterType,
}) => {
  const history = useHistory();

  const handleClick = () => {
    if (filterType) {
      // Navigate to My Library with the specific filter applied
      history.push({
        pathname: '/my-library',
        state: {
          activeFilter: filterType,
          applyFilter: true,
        },
      });
    } else {
      // Navigate to My Library without any filter
      history.push('/my-library');
    }
  };

  return (
    <IonCard
      className="modern-stat-widget"
      disabled={editing}
      onClick={handleClick}
    >
      <IonCardContent className="stat-widget-content">
        <IonIcon icon={icon} className="stat-icon" />
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
      </IonCardContent>
    </IonCard>
  );
};

// Individual widgets using the shared hook
export const TotalFilesWidget: React.FC<{ editing: boolean }> = ({
  editing,
}) => {
  const { stats, loading } = useLibraryStats();

  const displayValue = loading ? (
    <IonSpinner name="crescent" />
  ) : stats ? (
    (stats.totalFiles?.toLocaleString() ?? '0')
  ) : (
    '0'
  );

  return (
    <IonCard
      className="modern-stat-widget view-library-card"
      disabled={editing}
      routerLink="/my-library"
    >
      <IonCardContent className="stat-widget-content">
        <IonIcon icon={libraryOutline} className="stat-icon" />
        <div className="stat-value">{displayValue}</div>
        <div className="stat-label">My Library</div>
      </IonCardContent>
    </IonCard>
  );
};

export const ArtistsWidget: React.FC<{ editing: boolean }> = ({ editing }) => {
  const { stats, loading } = useLibraryStats();

  const displayValue = loading ? (
    <IonSpinner name="crescent" />
  ) : stats ? (
    (stats.totalArtists?.toLocaleString() ?? '0')
  ) : (
    '0'
  );

  return (
    <StatWidget
      editing={editing}
      icon={peopleOutline}
      value={displayValue}
      label="Artists"
      filterType="artists"
    />
  );
};

export const GenresWidget: React.FC<{ editing: boolean }> = ({ editing }) => {
  const { stats, loading } = useLibraryStats();

  const displayValue = loading ? (
    <IonSpinner name="crescent" />
  ) : stats ? (
    (stats.totalGenres?.toLocaleString() ?? '0')
  ) : (
    '0'
  );

  return (
    <StatWidget
      editing={editing}
      icon={pricetagsOutline}
      value={displayValue}
      label="Genres"
      filterType="genres"
    />
  );
};

export const DuplicatesWidget: React.FC<{ editing: boolean }> = ({
  editing,
}) => {
  const { stats, loading } = useLibraryStats();
  const history = useHistory();

  const displayValue = loading ? (
    <IonSpinner name="crescent" />
  ) : stats ? (
    (stats.duplicates?.toLocaleString() ?? '0')
  ) : (
    '0'
  );

  const handleClick = async () => {
    try {
      // Get all tracks with duplicates filter (original working implementation)
      const response = await new Promise<any>((resolve) => {
        once('my-music/get-paginated', resolve);
        run('my-music/get-paginated', {
          page: 0,
          limit: 10000, // Get a large number to ensure we get all duplicates
          filters: { filterType: 'duplicates', includeIgnored: true },
          sortField: 'id',
          sortOrder: 'ASC',
        });
      });

      if (response && response.tracks) {
        // Calculate duplicate groups from the filtered tracks
        const { calculateDuplicateGroups } = await import(
          '../../../utils/calculateDuplicateGroups'
        );
        const duplicateGroups = calculateDuplicateGroups(response.tracks);

        // Navigate directly to the duplicate resolution page with the data
        history.push({
          pathname: '/resolve-duplicates',
          state: {
            duplicateGroups: duplicateGroups,
            allTracks: response.tracks,
          },
        });
      } else {
        console.warn('No duplicate tracks found');
        // Show a toast or navigate to My Library with a message
        history.push('/my-library');
      }
    } catch (error) {
      console.error('Error fetching duplicate data:', error);
      // Navigate to My Library as fallback
      history.push('/my-library');
    }
  };

  return (
    <IonCard
      className="modern-stat-widget"
      disabled={editing}
      onClick={handleClick}
    >
      <IonCardContent className="stat-widget-content">
        <IonIcon icon={warningOutline} className="stat-icon" />
        <div className="stat-value">{displayValue}</div>
        <div className="stat-label">Duplicates</div>
      </IonCardContent>
    </IonCard>
  );
};

export const HealthReportWidget: React.FC<{ editing: boolean }> = ({
  editing,
}) => {
  const { healthScore, loading } = useLibraryStats();
  const history = useHistory();

  const displayValue = loading ? (
    <IonSpinner name="crescent" />
  ) : healthScore !== null ? (
    `${healthScore}%`
  ) : (
    'N/A'
  );

  const handleClick = () => {
    // Navigate directly to the health report page
    history.push('/my-library/health-report');
  };

  return (
    <IonCard
      className="modern-stat-widget"
      disabled={editing}
      onClick={handleClick}
    >
      <IonCardContent className="stat-widget-content">
        <IonIcon icon={medkitOutline} className="stat-icon" />
        <div className="stat-value">{displayValue}</div>
        <div className="stat-label">Health</div>
      </IonCardContent>
    </IonCard>
  );
};

// Main overview widget showing all stats + navigation button
export interface LibraryOverviewWidgetProps {
  editing: boolean;
}

const formatStat = (value: number | null | undefined, loading: boolean) => {
  if (loading) return <LibraryMetricValueSkeleton />;
  if (value == null) return '0';
  return value.toLocaleString();
};

export const LibraryOverviewWidget: React.FC<LibraryOverviewWidgetProps> = ({
  editing,
}) => {
  const { stats, healthScore, loading } = useLibraryStats();
  const history = useHistory();

  const metrics = [
    {
      icon: libraryOutline,
      value: formatStat(stats?.totalFiles, loading),
      label: 'My Library',
      to: '/my-library',
    },
    {
      icon: peopleOutline,
      value: formatStat(stats?.totalArtists, loading),
      label: 'Artists',
      to: '/my-library',
      state: { activeFilter: 'artists', applyFilter: true },
    },
    {
      icon: pricetagsOutline,
      value: formatStat(stats?.totalGenres, loading),
      label: 'Genres',
      to: '/my-library',
      state: { activeFilter: 'genres', applyFilter: true },
    },
    {
      icon: warningOutline,
      value: formatStat(stats?.duplicates, loading),
      label: 'Duplicates',
      to: '/resolve-duplicates',
    },
    {
      icon: medkitOutline,
      value: loading ? (
        <LibraryMetricValueSkeleton />
      ) : healthScore !== null ? (
        `${healthScore}%`
      ) : (
        'N/A'
      ),
      label: 'Health',
      to: '/my-library/health-report',
    },
  ];

  return (
    <div className="library-metrics-strip">
      {metrics.map((metric) => (
        <button
          key={metric.label}
          type="button"
          className="library-metric"
          disabled={editing}
          onClick={() =>
            history.push({ pathname: metric.to, state: metric.state })
          }
        >
          <IonIcon icon={metric.icon} className="library-metric-icon" />
          <span className="library-metric-copy">
            <span className="library-metric-value">{metric.value}</span>
            <span className="library-metric-label">{metric.label}</span>
          </span>
        </button>
      ))}
    </div>
  );
};

// Compact Library Stats Widget for Dashboard
export const LibraryStatsCompactWidget: React.FC<{ editing: boolean }> = ({
  editing,
}) => {
  const { stats, healthScore, loading } = useLibraryStats();
  const history = useHistory();

  const handleClick = () => {
    history.push('/my-library');
  };

  return (
    <IonCard
      className="library-stats-compact"
      disabled={editing}
      onClick={handleClick}
    >
      <IonCardContent className="compact-stats-content">
        <div className="compact-stats-header">
          <IonIcon icon={libraryOutline} className="compact-header-icon" />
          <span className="compact-title">My Library</span>
        </div>
        <div className="compact-stats-grid">
          <div className="compact-stat">
            <span className="compact-value">
              {loading ? (
                <IonSpinner name="dots" />
              ) : (
                (stats?.totalFiles?.toLocaleString() ?? '0')
              )}
            </span>
            <span className="compact-label">Tracks</span>
          </div>
          <div className="compact-stat">
            <span className="compact-value">
              {loading ? (
                <IonSpinner name="dots" />
              ) : (
                (stats?.totalArtists?.toLocaleString() ?? '0')
              )}
            </span>
            <span className="compact-label">Artists</span>
          </div>
          <div className="compact-stat">
            <span className="compact-value">
              {loading ? (
                <IonSpinner name="dots" />
              ) : healthScore !== null ? (
                `${healthScore}%`
              ) : (
                'N/A'
              )}
            </span>
            <span className="compact-label">Health</span>
          </div>
        </div>
      </IonCardContent>
    </IonCard>
  );
};
